import { useMemo } from 'react';

import { useSession } from '@ais-ui/websockets';
import { UseQueryResult, useQueryClient } from '@tanstack/react-query';

import {
  useTrackedQuery as useQuery,
  useTrackedMutation as useMutation,
} from '@ais-ui/performance-tracking';

import { useCredentials } from '@ais-ui/auth';
import { useBasePath } from '@ais-ui/custom-hooks';

import {
  Configuration,
  DefinitionGet200Response,
  DefinitionIdPutRequest,
  DefinitionPostRequest,
  EventdetectorApi,
} from '@ais-ui/api-spec/eventdetector';
import { getBasePath } from './getAISBasePath';
import { getAuthHeaderFromCredentials } from '@ais-ui/utils';
import { useApiClientWrapper } from './apiClientWrapper';

export const useEventdetectorApi = () => {
  const credentials = useCredentials();
  const { sessionId } = useSession();
  const { protocol, hostname } = useBasePath();
  const apiClientWrapper = useApiClientWrapper();

  let basePath = getBasePath();
  let configuration: Configuration;
  if (hostname.includes('localhost')) {
    const username = credentials?.username || '';
    const password = credentials?.password || '';

    const authHeader = getAuthHeaderFromCredentials(username, password);
    configuration = new Configuration({
      basePath: `${basePath}api/eventdetector`,
      headers: {
        'X-Application-Name': 'marinepulse',
        Authorization: authHeader,
      },
    });
  } else {
    basePath = `${basePath}aisweb/api/eventdetector`;
    configuration = new Configuration({
      basePath: basePath,
      headers: {
        'Aisweb-Session': sessionId,
        'X-Application-Name': 'marinepulse',
      },
    });
  }

  const eventdetectorApi = useMemo(
    () => new EventdetectorApi(configuration),
    [configuration]
  );
  return apiClientWrapper(eventdetectorApi) as EventdetectorApi;
};

export const useFetchAllEventdetectorDefinitions = (
  enabled = true
): UseQueryResult<DefinitionGet200Response> => {
  const eventdetectorApi = useEventdetectorApi();

  return useQuery({
    queryKey: ['eventdetectorAllDefinitions'],
    queryFn: async () => {
      return await eventdetectorApi.definitionGet({ useUid: true });
    },
    enabled,
  }, 'eventdetector:listDefinitions');
};

export const useConditionalEventdetectorDefinitionsFetch = (
  shouldFetch: boolean
): UseQueryResult<DefinitionGet200Response, Error> => {
  const eventdetectorApi = useEventdetectorApi();

  return useQuery({
    queryKey: ['eventdetectorAllDefinitions'],
    queryFn: async () => {
      if (shouldFetch) {
        return await eventdetectorApi.definitionGet({ useUid: true });
      } else {
        // Return a default value when shouldFetch is false
        return Promise.resolve({ records: [] } as DefinitionGet200Response);
      }
    },
  }, 'eventdetector:conditionalDefinitions');
};

export const useExportFetchUrl = () => {
  const eventdetectorApi = useEventdetectorApi();

  const fetchExportUrl = async (start: string, end: string) => {
    try {
      const data = await eventdetectorApi.exportGet({
        start: start,
        end: end,
        serveruid: 'd4bd4710-1765-4476-8969-09ab9d0b676f',
      });
      return data;
    } catch (error) {
      console.error('Error fetching shape:', error);
      throw error;
    }
  };

  return fetchExportUrl;
};

export const useCreateEventdetector = () => {
  const eventdetectorApi = useEventdetectorApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestBody: DefinitionPostRequest) => {
      return eventdetectorApi.definitionPost(requestBody);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['eventdetectorAllDefinitions'],
      });
    },
  }, 'eventdetector:create');
};

export const useEditEventdetector = () => {
  const eventdetectorApi = useEventdetectorApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (requestBody: DefinitionIdPutRequest) =>
      eventdetectorApi.definitionIdPut(requestBody),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['eventdetectorAllDefinitions'],
      });
    },
  }, 'eventdetector:edit');
};

export const useDeleteEventdetector = () => {
  const eventdetectorApi = useEventdetectorApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => eventdetectorApi.definitionIdDelete({ id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['eventdetectorAllDefinitions'],
      });
    },
  }, 'eventdetector:delete');
};

export const useFetchEvent = (
  eventId: any
): UseQueryResult<DefinitionGet200Response> => {
  const eventdetectorApi = useEventdetectorApi();
  return useQuery({
    queryKey: ['eventdetectorAllDefinitions'],
    queryFn: async () => {
      return await eventdetectorApi.getEventDetails(eventId);
    },
  }, 'eventdetector:getEvent');
};

export const useFetchEventFunction = () => {
  const eventdetectorApi = useEventdetectorApi();

  return (eventId: any) => eventdetectorApi.getEventDetails(eventId);
};
